import { FC } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AdRoutes } from "../../../constants/ad-routes";
import { Box, Button, Divider, Typography } from "@mui/material";
import { makeSxStyles } from "ui";
import { toast } from "react-toastify";
import { useAdminServiceAlertsControllerSendServiceAlert } from "service";
import NotificationFormServiceAlert from "./notification-form-service-alert";
import useUnsavedChangesBlocker from "../../../shared/hooks/useUnsavedChangesBlocker";
import { useServiceAlertForm } from "./hooks/useServiceAlertForm";
import DashboardContentLayout from "../components/dashboard-content-layout";
import Navbar from "../../../components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import { useDialogStore } from "../../../state/dialog-store";

const EditServiceAlertPage: FC = () => {
  const { notificationId } = useParams<{
    notificationId: string;
  }>();
  const styles = useStyles();
  const navigate = useNavigate();
  const { setDialog } = useDialogStore();

  const { mutate: sendNotification } =
    useAdminServiceAlertsControllerSendServiceAlert();

  const {
    values,
    handleChange,
    handleBlur,
    dirty,
    isValid,
    touched,
    errors,
    submitForm,
    isLoading,
    data,
    resetForm,
  } = useServiceAlertForm({
    serviceAlertId: notificationId!,
    onSuccess: () => {
      resetForm();
      setTimeout(() => {
        navigate(`${AdRoutes.notifications}?tab=serviceAlerts`);
      }, 10);
    },
  });

  useUnsavedChangesBlocker(dirty);

  const onSendAlert = () => {
    setDialog({
      title: "Send Notification",
      description: `Are you sure you want to send this notification now?`,
      confirmText: "Send now",
      cancelText: "Cancel",
      onConfirm: async (onClose) => {
        handleSendAlert(notificationId!);
        onClose();
      },
    });
  };

  const handleSendAlert = (id: string) => {
    sendNotification(
      { pathParams: { serviceAlertId: id } },
      {
        onSuccess: () => {
          navigate(`${AdRoutes.notifications}?tab=serviceAlerts`);
          toast.success("Service Alert Sent");
        },
      },
    );
  };

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Notification Center",
                url: AdRoutes.notifications,
              },
              {
                label: "Edit Service Alert",
              },
            ]}
          />
          <HeaderItemsRight>
            <Button
              disabled={isLoading || !dirty || !isValid}
              variant="contained"
              onClick={submitForm}
              data-testid="save-notification-button"
            >
              Save Notification
            </Button>
            <Button
              disabled={isLoading || !isValid}
              variant="contained"
              onClick={onSendAlert}
              data-testid="send-notification-button"
            >
              Send Now
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <NotificationFormServiceAlert
        values={values}
        handleChange={handleChange}
        handleBlur={handleBlur}
        touched={touched}
        errors={errors}
      />
      <Divider sx={{ mt: "40px" }} />
      <Box sx={styles.customerDetailContainer}>
        <Typography fontWeight={600}>Recipients</Typography>
        <Typography fontWeight={400}>{data?.data?.customer.name}</Typography>
        <Typography fontWeight={600} alignSelf={"flex-start"}>
          Address
        </Typography>
        <Typography fontWeight={400} alignSelf={"flex-start"}>
          {data?.data?.location.address}
        </Typography>
      </Box>
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  customerDetailContainer: {
    mt: "40px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    rowGap: "16px",
    maxWidth: "50%",
    alignItems: "center",
  },
  flexContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "12px",
  },
  dayCheckbox: {
    position: "relative",
    minWidth: "38px",
    "&:before": {
      //zIndex: -1,
      position: "absolute",
      content: "''",
      display: "inline-block",
      top: "1px",
      width: "38px",
      height: "38px",
      borderRadius: "100px",
      border: "1px solid #8B9198",
    },
    "&.Mui-checked": {
      "&:before": {
        backgroundColor: "#004b3b40",
        borderColor: "#005140",
      },
    },
  },
  dialog: {
    ".MuiDialog-paper": {
      minWidth: "500px",
    },
  },
  button: {
    width: 140,
  },
}));

export default EditServiceAlertPage;
